import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useMileage, {ChallengeType} from '@di-shared/features/mileage/useMileage';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import React, {Suspense} from 'react';
import {DrivingInfoEcoMileageBannerProps} from '@di-shared/features/history/driving-info/DrivingInfoEcoMileageBanner/index';
import {ErrorBoundary} from 'react-error-boundary';

export default function ({vin, averageDistance}: DrivingInfoEcoMileageBannerProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          averageDistance={averageDistance}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, averageDistance}: DrivingInfoEcoMileageBannerProps) {
  const {t} = useInferableTranslation();
  const {getChallengeType, challengeDDay} = useMileage(vin);
  const {odometer} = useOdometer(vin);
  const challengeType = getChallengeType(odometer);

  const challengeMileage = (() => {
    switch (challengeType) {
      case ChallengeType.SEVEN:
        return 7;
      case ChallengeType.FIVE:
        return 5;
      case ChallengeType.TRREE:
        return 3;
      case ChallengeType.TWO:
        return 2;
      default:
        return 0;
    }
  })();

  /**
   * CHALLENGE_UNAVAILABLE, UPDATE_STATUS, ODOMETER_ERROR 인 경우에는 미노출
   */
  if (challengeMileage === 0) {
    return null;
  }

  /**
   * 획득가능: (잔여도전기간/30) * 내 평균 주행거리 ≤ 잔여 주행거리일 경우
   * 획득실패: (잔여도전기간/30) * 내 평균 주행거리 > 잔여 주행거리일 경우
   */
  const isSuccessEcoMileage = (challengeDDay / 30) * (challengeMileage * 100) >= averageDistance;

  return (
    <div className="driving_banner">
      <LinkTo
        to={ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin)}
        className="driving_banner_box"
      >
        <span className="driving_banner_txtbox">
          <strong className="driving_banner_title body_medium_b">{t('T10068')}</strong>
          <span className="driving_banner_desc body_small_r">
            {isSuccessEcoMileage ? t('T10069_$mileage', {mileage: challengeMileage}) : t('T10194_$mileage', {mileage: challengeMileage})}
          </span>
        </span>
        <span className="driving_banner_imgbox">
          {isSuccessEcoMileage ? (
            <ImageSelector
              className="driving_banner_img"
              alt="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
            />
          ) : (
            <ImageSelector
              className="driving_banner_img"
              alt="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage_fail.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage_fail.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage_fail.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage_fail.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage_fail.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage_fail.png')}
            />
          )}
        </span>
      </LinkTo>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="driving_banner skeleton">
      <div className="driving_banner_box">
        <span className="driving_banner_txtbox">
          <strong className="driving_banner_title body_medium_b">{t('T10068')}</strong>
          <span className="driving_banner_desc body_small_r">{t('T10069_$mileage', {mileage: 0})}</span>
        </span>
        <span className="driving_banner_imgbox">
          <ImageSelector
            className="driving_banner_img"
            alt="driving_banner_img"
            kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
          />
        </span>
      </div>
    </div>
  );
}
